import React, { FC, useCallback, useEffect, useState } from 'react';

import classNames from 'classnames';

import { SelectionList } from '../../../models/SelectionList';
import { PriceTier } from '../../../models/PriceTier';
import { SelectionListDropDown } from '../common/SelectionListDropDown';
import { PriceSelector, PriceSelectorHelpTooltip } from './PriceSelector';
import { useControlledState } from '../useControlledState';
import { Button, ButtonSize, ButtonTheme } from '../common/Button';

import './BulkActionButtons.scss';
import { BulkAddProductsToSelectionListResponse, BulkAddProductsToShoppingCartResponse } from '../../store/api';
import { ListActionTarget } from '../../../models/ListActionTarget';
import { useListContentChecks } from '../common/useListContentChecks';
import { CheckBeforeAddingModal } from '../common/CheckBeforeAddingModal';
import { AddToCartOptions, AddToListOptions, isAddToListOptions } from '../../../models/ListContentCheckOptions';
import { UnavailableProductsModal } from '../common/UnavailableProductsModal';
import { ACTION_ADD } from '../../store/constants';
import { SubscriptionPeriodType } from '../../../models/Article';
import { ConfirmOptionsModal, DEFAULT_SUBSCRIPTION_PERIOD_TYPE } from '../common/ConfirmOptionsModal';

type Props = {
    readonly selectionLists: ReadonlyArray<SelectionList>;
    readonly listLabel: string;
    readonly cartLabel: string;
    readonly theme?: 'language';
    readonly onCreateNewListClicked: (newSelectionListName: string) => void;
    readonly onBulkAddToList: (
        list: SelectionList,
        priceTier: PriceTier,
        subscriptionPeriodType: SubscriptionPeriodType,
        performChecks: boolean
    ) => Promise<BulkAddProductsToSelectionListResponse>;
    readonly onBulkAddToCart: (
        priceTier: PriceTier,
        subscriptionPeriodType: SubscriptionPeriodType,
        performChecks: boolean
    ) => Promise<BulkAddProductsToShoppingCartResponse>;
    readonly onChange?: (newValue: PriceTier) => void;
    readonly value?: PriceTier;
    readonly onlyEpressProducts: boolean;
};

export const BulkActionButtons: FC<Props> = ({
    selectionLists,
    listLabel,
    cartLabel,
    theme,
    onCreateNewListClicked,
    onBulkAddToList,
    onBulkAddToCart,
    onChange,
    onlyEpressProducts,
    value
}) => {
    const [priceTier, setPriceTier] = useControlledState<PriceTier>(
        onlyEpressProducts ? { lendingModel: 'SIM', level: 'MIN' } : { lendingModel: 'OCOU', level: 'MIN' },
        value,
        onChange
    );

    // This is to change the price tier into the default value when the value of "onlyEpressProducts" changes
    useEffect(() => {
        setPriceTier(currentPriceTier => {
            if (!onlyEpressProducts && currentPriceTier.lendingModel === 'SIM' && currentPriceTier.level === 'MIN') {
                return { lendingModel: 'OCOU', level: 'MIN' };
            }
            if (onlyEpressProducts && currentPriceTier.lendingModel === 'OCOU' && currentPriceTier.level === 'MIN') {
                return { lendingModel: 'SIM', level: 'MIN' };
            }
            return currentPriceTier;
        });
    }, [onlyEpressProducts, setPriceTier]);

    const [addOptions, setAddOptions] = useState<AddToListOptions | AddToCartOptions | undefined>(undefined);
    const [addingToCart, setAddingToCart] = useState(false);

    const onBulkAdd = useCallback(
        (
            performChecks: boolean,
            options: AddToListOptions | AddToCartOptions
        ): Promise<BulkAddProductsToSelectionListResponse | BulkAddProductsToShoppingCartResponse> =>
            isAddToListOptions(options)
                ? onBulkAddToList(options.list, priceTier, options.subscriptionPeriodType, performChecks)
                : onBulkAddToCart(priceTier, options.subscriptionPeriodType, performChecks),
        [onBulkAddToList, onBulkAddToCart, priceTier]
    );

    const { changePrice, getCheckBeforeAddingModalProps, getUnavailableProductsModalProps } = useListContentChecks<
        AddToListOptions | AddToCartOptions
    >({ onAction: onBulkAdd });

    const handleBulkAddToList = useCallback(
        async (list: SelectionList) => {
            if (onlyEpressProducts) {
                setAddOptions({
                    target: ListActionTarget.List,
                    priceTier,
                    subscriptionPeriodType: DEFAULT_SUBSCRIPTION_PERIOD_TYPE,
                    list,
                    action: ACTION_ADD
                });
            } else {
                await changePrice({
                    target: ListActionTarget.List,
                    list,
                    priceTier,
                    subscriptionPeriodType: DEFAULT_SUBSCRIPTION_PERIOD_TYPE,
                    action: ACTION_ADD
                });
            }
        },
        [changePrice, priceTier, onlyEpressProducts]
    );

    const handleBulkAddToCart = useCallback(async () => {
        if (onlyEpressProducts) {
            setAddOptions({
                target: ListActionTarget.Cart,
                priceTier,
                subscriptionPeriodType: DEFAULT_SUBSCRIPTION_PERIOD_TYPE,
                action: ACTION_ADD
            });
        } else {
            setAddingToCart(true);
            try {
                await changePrice({
                    target: ListActionTarget.Cart,
                    priceTier,
                    subscriptionPeriodType: DEFAULT_SUBSCRIPTION_PERIOD_TYPE,
                    action: ACTION_ADD
                });
            } finally {
                setAddingToCart(false);
            }
        }
    }, [changePrice, priceTier, onlyEpressProducts]);

    return (
        <div className={classNames('bulk-action-buttons', theme)}>
            <PriceSelectorHelpTooltip />
            <PriceSelector value={priceTier} onChange={setPriceTier} />
            <SelectionListDropDown
                size={ButtonSize.LARGE}
                theme={ButtonTheme.POSITIVE}
                label={listLabel}
                lists={selectionLists}
                onCreateNewListClicked={onCreateNewListClicked}
                onListClick={handleBulkAddToList}
                disabled={false}
            />
            <Button
                size={ButtonSize.LARGE}
                theme={ButtonTheme.POSITIVE}
                icon="shopping_cart"
                text={cartLabel}
                onClick={handleBulkAddToCart}
                isLoading={addingToCart}
                disabled={addingToCart}
            />
            <CheckBeforeAddingModal {...getCheckBeforeAddingModalProps()} />
            <UnavailableProductsModal {...getUnavailableProductsModalProps()} />
            <ConfirmOptionsModal
                options={addOptions}
                changePrice={changePrice}
                showUsageConditionOption={false}
                showSubscriptionStartDateOption={onlyEpressProducts}
                closeModal={() => setAddOptions(undefined)}
            />
        </div>
    );
};
